/**
 * Client-side helper: turn a list of cart items + shipping form data into a
 * `CheckoutIntent` ready to POST to `/api/paypal-orders/create`.
 *
 * The server is the money authority — this helper only ferries item ids,
 * quantities, the buyer's chosen shipping method, the ship-to address, and a
 * discriminated `context` tag declaring which DB column the server should
 * read as unit price (listings.price / offers.amount / auction_highest_bid).
 * No amounts, no fees, no platform_fees. If the server resolver rejects
 * (stale listing, removed shipping method, banned country, ...), the buyer
 * sees PAYMENT_BREAKDOWN_MISMATCH and the cart should refetch.
 *
 * Shared between the cart sheet, chat checkout, offer checkout and the
 * auction-winner sheet so all four surfaces send an identical request shape
 * — but each is responsible for declaring its `context.kind` correctly.
 *
 * @see .tondocs/payments/CHECKOUT_INTENT_CONTRACT.md
 * @see .tondocs/payments/INTENT_CONTEXT_KIND.md — why context is required
 * @see lib/payments/checkout-intent.ts
 */

import type {
  CheckoutIntentDraft,
  CheckoutIntentExternalShippingSelection,
  CheckoutIntentFlatRateItem,
  CheckoutIntentLiveRateItem,
  CheckoutItemContext,
} from "@/lib/payments/checkout-intent";
import type { CartItemData } from "@/components/cart/types";
import type { ShippingFormData } from "@/components/cart/shipping-form";

/**
 * Required context-per-line map. Keyed by `CartItemData.id`. Each surface
 * picks the right kind for its flow:
 *   - cart fixed-price / chat buy-now → `{ kind: 'listing' }`
 *   - offer settlement                → `{ kind: 'offer', offerId }`
 *   - auction win                     → `{ kind: 'auction' }`
 *
 * Required, not optional — a missing entry returns `null` and the caller
 * gates the PayPal button. This is the design choice from
 * INTENT_CONTEXT_KIND.md "Decision — required, not optional": pre-release
 * mobile + atomic web cutover = no legacy clients, so the cleaner shape
 * lands free.
 */
export type ContextByItemId = Record<string, CheckoutItemContext>;

/**
 * @param sellerId `profiles.id` of the single seller this checkout pays.
 *   Single-seller invariant — caller derives from the active SellerGroup.
 *   @see .tondocs/payments/SINGLE_SELLER_INVARIANT_SPEC.md
 * @param externalShipping Optional live-rate carrier-provider selection. When
 *   the active seller is in `shipping_mode='live_rate'`, the buyer picks a
 *   rate in the address-first sub-flow; the selection is threaded into the
 *   intent so the server can re-validate at create-order time and route the
 *   money. Absent for legacy (flat-rate) sellers. Single-seller invariant —
 *   `sellerProfileId` is implicit (= `intent.sellerId`).
 *   @see .tondocs/shippo/PLAN.md §D WS4 (per-seller routing)
 *   @see packages/contract/src/checkout-intent.ts CheckoutIntent.externalShipping
 */
export function buildCheckoutIntent(
  items: CartItemData[],
  formData: ShippingFormData,
  currencyCode: string,
  contextByItemId: ContextByItemId,
  sellerId: string,
  externalShipping?: CheckoutIntentExternalShippingSelection,
): CheckoutIntentDraft | null {
  if (items.length === 0) return null;
  if (!sellerId) return null;

  // PR-E Move 2 (Sibling-F · F2): the `kind` discriminator is at the root of
  // CheckoutIntent now (was per-item). Items are uniform within an intent —
  // either all `flat_rate` (with `shippingMethodId`) or all `live_rate`
  // (without). The branch decision is taken once at intent construction
  // time; we accumulate per-branch items in a strictly typed local list.
  const isLiveRate = items.some((it) => it.shippingMode === "live_rate");
  const allLiveRate = items.every((it) => it.shippingMode === "live_rate");
  // Defensive: under the single-seller invariant + per-seller shipping_mode
  // a mixed intent is impossible. If observed, refuse to build — resolver
  // will reject anyway and we prefer null over a malformed intent.
  if (isLiveRate && !allLiveRate) return null;

  const baseShape = {
    currency: currencyCode,
    sellerId,
    shippingAddress: {
      firstName: formData.firstName,
      lastName: formData.lastName,
      address: formData.address,
      apartment: formData.apartment,
      city: formData.city,
      state: formData.state,
      postalCode: formData.postalCode,
      country: formData.country,
      email: formData.email,
      phoneCountryCode: formData.phoneCountryCode,
      phoneNumber: formData.phoneNumber,
    },
  };

  if (isLiveRate) {
    // Live-rate listings carry no per-item shipping method — the buyer's
    // locked carrier rate lives at intent root (`externalShipping`) under
    // the single-seller invariant. The caller (`PayPalCheckout`) owns the
    // live-rate sub-flow FSM and folds the lock into `effectiveIntent`
    // before any POST.
    //
    // Return type is `CheckoutIntentDraft`: `externalShipping` is OPTIONAL
    // at this layer. The wire schema (`CheckoutIntentSchema`) still pins it
    // REQUIRED — illegal-state-unrepresentable on the wire stays intact.
    // The cart-layer caller cannot supply the lock (FSM lives one layer
    // down); returning null here was the source of the 2026-06-30 outage
    // where `intent === null` disabled PayPal SDK + Stripe button for every
    // live_rate seller after the F2 root-discriminator refactor.
    //
    // @see CheckoutIntentLiveRateDraft — type declaration + rationale
    // @see [[feedback_left_fix_audit_downstream_branches]]
    const liveItems: CheckoutIntentLiveRateItem[] = [];
    for (const item of items) {
      const context = contextByItemId[item.id];
      if (!context) return null;
      liveItems.push({
        listingId: item.listingId,
        quantity: item.quantity,
        context,
      });
    }
    return {
      ...baseShape,
      kind: "live_rate",
      items: liveItems,
      ...(externalShipping ? { externalShipping } : {}),
    };
  }

  const flatItems: CheckoutIntentFlatRateItem[] = [];
  for (const item of items) {
    const context = contextByItemId[item.id];
    if (!context) return null;
    const shippingMethodId = item.selectedShippingMethod?.id;
    if (!shippingMethodId) return null;
    flatItems.push({
      listingId: item.listingId,
      quantity: item.quantity,
      shippingMethodId,
      context,
    });
  }
  return {
    ...baseShape,
    kind: "flat_rate",
    items: flatItems,
  };
}
